import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp, CalendarCheck, XCircle, DollarSign, Trash2, Plus, Save, Phone, Mail, Inbox, MapPin, Calendar, Clock, User, Plane, Users as UsersIcon, Luggage, FileText, CreditCard, Hash, Eye, UserCheck, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUSES = ["pending_payment", "confirmed", "completed", "cancelled"];
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const card = "border border-white/10 bg-[#121212] p-6";
const fld = "bg-secondary border-white/10 rounded-none h-11";

function Stat({ icon: Icon, label, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
      className={`${card} text-left w-full transition-colors hover:border-primary/50 cursor-pointer`}
    >
      <Icon className="text-primary mb-3" size={22} />
      <div className="text-3xl font-heading text-foreground">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{label} <span className="text-primary/60">›</span></div>
    </button>
  );
}

const STATUS_STYLES = {
  pending_payment: "border-yellow-500/40 text-yellow-400",
  confirmed: "border-green-500/40 text-green-400",
  completed: "border-blue-500/40 text-blue-400",
  cancelled: "border-red-500/40 text-red-400",
};

function Row({ icon: Icon, label, value, testid }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0" data-testid={testid}>
      {Icon && <Icon size={16} className="text-primary mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground break-words">{value}</div>
      </div>
    </div>
  );
}

function BookingDetailsDialog({ booking, onOpenChange }) {
  if (!booking) return null;
  const q = booking.quote || {};
  const isHourly = !!q.hours;
  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);
  return (
    <Dialog open={!!booking} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-details-dialog"
        className="bg-[#0d0d0d] border border-white/10 text-foreground max-w-2xl max-h-[90vh] overflow-y-auto rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-primary flex items-center flex-wrap gap-3">
            #{booking.confirmation_code}
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${STATUS_STYLES[booking.status] || "border-white/20 text-muted-foreground"}`}>
              {(booking.status || "").replace("_", " ")}
            </span>
            {booking.payment_status && (
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${booking.payment_status === "paid" ? "border-green-500/40 text-green-400" : "border-white/20 text-muted-foreground"}`}>
                {booking.payment_status}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Full trip details for reservation {booking.confirmation_code}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-x-6 mt-2">
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-primary mt-2 mb-1">Customer</h4>
            <Row icon={User} label="Name" value={booking.customer_name} testid="detail-name" />
            <Row icon={Mail} label="Email" value={<a href={`mailto:${booking.customer_email}`} className="hover:text-primary">{booking.customer_email}</a>} testid="detail-email" />
            <Row icon={Phone} label="Phone" value={<a href={`tel:${booking.customer_phone}`} className="hover:text-primary">{booking.customer_phone}</a>} testid="detail-phone" />
            <Row icon={Plane} label="Flight Number" value={booking.flight_number} testid="detail-flight" />
            <Row icon={FileText} label="Notes" value={booking.notes} testid="detail-notes" />
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-primary mt-2 mb-1">Trip</h4>
            <Row icon={Calendar} label="Pickup Date" value={q.date} testid="detail-date" />
            <Row icon={Clock} label="Pickup Time" value={q.time} testid="detail-time" />
            <Row icon={MapPin} label="Pickup Address" value={q.pickup} testid="detail-pickup" />
            {!isHourly && <Row icon={MapPin} label="Drop-off Address" value={q.dropoff} testid="detail-dropoff" />}
            {isHourly && <Row icon={Clock} label="Duration" value={`${q.hours} hours`} testid="detail-hours" />}
            {Array.isArray(q.stops) && q.stops.length > 0 && (
              <Row icon={MapPin} label={`Extra Stops (${q.stops.length})`} value={q.stops.join(" • ")} testid="detail-stops" />
            )}
            <Row label="Service Type" value={q.service_type} testid="detail-service" />
            <Row label="Distance" value={q.distance_miles != null ? `${Number(q.distance_miles).toFixed(1)} mi` : null} testid="detail-distance" />
            <Row label="Est. Duration" value={q.duration_text} testid="detail-duration-text" />
            <Row label="Meet & Greet" value={q.meet_greet ? "Yes" : null} testid="detail-mg" />
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-primary mt-4 mb-1">Vehicle</h4>
            <Row label="Vehicle" value={q.vehicle_name} testid="detail-vehicle" />
            <Row icon={UsersIcon} label="Passengers" value={q.passengers} testid="detail-pax" />
            {q.luggage != null && <Row icon={Luggage} label="Luggage" value={q.luggage} testid="detail-luggage" />}
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-primary mt-4 mb-1">Payment</h4>
            <Row icon={DollarSign} label="Base Fare" value={money(q.base_fare)} testid="detail-base" />
            <Row label="Meet & Greet Fee" value={money(q.meet_greet_fee)} testid="detail-mg-fee" />
            <Row label="Stops Fee" value={money(q.stops_fee)} testid="detail-stops-fee" />
            <Row label="Gratuity" value={money(q.tip_amount ?? q.gratuity)} testid="detail-tip" />
            <Row label="Discount" value={q.discount ? `-${money(q.discount)}` : null} testid="detail-discount" />
            <Row label="Promo Code" value={q.promo_code} testid="detail-promo" />
            <Row icon={DollarSign} label="Total" value={<span className="text-primary text-lg font-heading">{money(q.total)}</span>} testid="detail-total" />
            <Row icon={CreditCard} label="Stripe Session" value={booking.stripe_session_id} testid="detail-stripe" />
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-primary mt-4 mb-1">Meta</h4>
            <Row icon={Hash} label="Booking ID" value={booking.id} testid="detail-id" />
            <Row icon={Calendar} label="Created" value={fmtDate(booking.created_at)} testid="detail-created" />
            <Row icon={Calendar} label="Updated" value={fmtDate(booking.updated_at)} testid="detail-updated" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ icon: Icon, label, value, testid }) {
  return (
    <div className={`${card}`} data-testid={testid}>
      <Icon className="text-primary mb-3" size={20} />
      <div className="text-2xl font-heading text-foreground">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function AnalyticsView({ analytics }) {
  if (!analytics) {
    return <div className={card} data-testid="analytics-loading">Loading analytics...</div>;
  }
  const a = analytics;
  const conv = a.total_bookings && a.unique_visitors
    ? Math.min(100, (a.total_bookings / a.unique_visitors) * 100).toFixed(1) + "%"
    : "—";
  const maxViews = Math.max(1, ...(a.views_by_day || []).map((d) => d.views));
  return (
    <div className="space-y-6" data-testid="analytics-view">
      <div>
        <h3 className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Traffic</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MiniStat icon={Eye} label="Views Today" value={a.views_today ?? 0} testid="stat-views-today" />
          <MiniStat icon={Eye} label="Views (7 days)" value={a.views_7d ?? 0} testid="stat-views-7d" />
          <MiniStat icon={Eye} label="Total Views" value={a.total_views ?? 0} testid="stat-views-total" />
          <MiniStat icon={UserCheck} label="Unique Visitors" value={a.unique_visitors ?? 0} testid="stat-unique-visitors" />
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Conversion</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MiniStat icon={CalendarCheck} label="Reservations" value={a.total_bookings ?? 0} testid="stat-a-bookings" />
          <MiniStat icon={CalendarCheck} label="Confirmed" value={a.confirmed ?? 0} testid="stat-a-confirmed" />
          <MiniStat icon={XCircle} label="Cancellations" value={a.cancelled ?? 0} testid="stat-a-cancelled" />
          <MiniStat icon={BarChart3} label="Visitors → Booking" value={conv} testid="stat-a-conversion" />
        </div>
      </div>

      <div className={card}>
        <h3 className="text-xs uppercase tracking-[0.2em] text-primary mb-4">Views (last 30 days)</h3>
        {(!a.views_by_day || a.views_by_day.length === 0) ? (
          <div className="text-sm text-muted-foreground" data-testid="analytics-chart-empty">No traffic recorded yet.</div>
        ) : (
          <div className="flex items-end gap-1 h-40" data-testid="analytics-chart">
            {a.views_by_day.map((d) => {
              const h = Math.max(4, Math.round((d.views / maxViews) * 100));
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.views} views · ${d.visitors} visitors`}>
                  <div className="w-full bg-primary/70 group-hover:bg-primary transition-colors" style={{ height: `${h}%` }} />
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-between text-[10px] text-muted-foreground mt-2 uppercase tracking-[0.15em]">
          <span>{a.views_by_day?.[0]?.date || ""}</span>
          <span>{a.views_by_day?.[a.views_by_day.length - 1]?.date || ""}</span>
        </div>
      </div>

      <div className={card}>
        <h3 className="text-xs uppercase tracking-[0.2em] text-primary mb-4">Top Pages</h3>
        {(!a.top_pages || a.top_pages.length === 0) ? (
          <div className="text-sm text-muted-foreground" data-testid="analytics-pages-empty">No page views yet.</div>
        ) : (
          <div className="space-y-2" data-testid="analytics-top-pages">
            {a.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0">
                <span className="text-sm text-foreground truncate">{p.path}</span>
                <span className="text-sm text-primary font-heading">{p.views}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [promos, setPromos] = useState([]);
  const [settings, setSettings] = useState(null);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [bookingFilter, setBookingFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newPromo, setNewPromo] = useState({ code: "", discount_pct: 10 });

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(err);
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(err);
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(err);
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(err);
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(err);
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(err);
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(err);
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(err);
  };
  useEffect(() => { loadAll(); }, []);

  const showBookings = (filter) => { setBookingFilter(filter); setActiveTab("bookings"); };
  const filterLabels = { all: "All bookings", confirmed: "Confirmed", cancelled: "Cancelled", paid: "Paid" };
  const filteredBookings = bookings.filter((b) =>
    bookingFilter === "all" ? true
    : bookingFilter === "paid" ? b.payment_status === "paid"
    : b.status === bookingFilter);

  const setBookingStatus = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success("Booking updated"); loadAll(); } catch (e) { err(e); }
  };
  const saveVehicle = async (v) => {
    try { await api.put(`/admin/vehicles/${v.id}`, v); toast.success(`${v.name} saved`); } catch (e) { err(e); }
  };
  const deleteVehicle = async (id) => {
    try { await api.delete(`/admin/vehicles/${id}`); toast.success("Vehicle removed"); loadAll(); } catch (e) { err(e); }
  };
  const saveSettings = async () => {
    try {
      const { id, ...body } = settings;
      await api.put("/admin/settings", body); toast.success("Pricing saved");
    } catch (e) { err(e); }
  };
  const addPromo = async () => {
    try { await api.post("/admin/promos", { ...newPromo, discount_pct: Number(newPromo.discount_pct), active: true }); toast.success("Promo added"); setNewPromo({ code: "", discount_pct: 10 }); loadAll(); } catch (e) { err(e); }
  };
  const togglePromo = async (p) => {
    try { await api.put(`/admin/promos/${p.code}`, { ...p, active: !p.active }); loadAll(); } catch (e) { err(e); }
  };
  const deletePromo = async (code) => {
    try { await api.delete(`/admin/promos/${code}`); toast.success("Promo removed"); loadAll(); } catch (e) { err(e); }
  };

  const updateVehField = (id, k, val) => setVehicles((vs) => vs.map((v) => v.id === id ? { ...v, [k]: val } : v));

  return (
    <div className="min-h-screen bg-background py-8 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm"><ArrowLeft size={16} /> Home</Link>
          <Button variant="ghost" data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }}>Logout</Button>
        </div>
        <h1 className="text-3xl sm:text-4xl text-foreground">Admin Console</h1>
        <p className="text-muted-foreground mb-8">Signed in as {user?.email}</p>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#121212] border border-white/10 rounded-none mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="fleet" data-testid="tab-fleet">Fleet</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing & Promos</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Stat icon={TrendingUp} label="Total Bookings" value={stats.total_bookings ?? "—"} onClick={() => showBookings("all")} />
              <Stat icon={CalendarCheck} label="Confirmed" value={stats.confirmed ?? "—"} onClick={() => showBookings("confirmed")} />
              <Stat icon={XCircle} label="Cancelled" value={stats.cancelled ?? "—"} onClick={() => showBookings("cancelled")} />
              <Stat icon={DollarSign} label="Revenue" value={stats.revenue != null ? `$${stats.revenue.toFixed(0)}` : "—"} onClick={() => showBookings("paid")} />
            </div>
          </TabsContent>

          {/* ANALYTICS */}
          <TabsContent value="analytics">
            <AnalyticsView analytics={analytics} />
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-muted-foreground">Showing:</span>
              <span className="text-sm text-primary uppercase tracking-wide" data-testid="booking-filter-label">{filterLabels[bookingFilter]}</span>
              {bookingFilter !== "all" && (
                <button type="button" data-testid="booking-filter-clear" onClick={() => setBookingFilter("all")}
                  className="text-xs text-muted-foreground hover:text-foreground underline">Show all</button>
              )}
            </div>
            <div className="space-y-3">
              {filteredBookings.length === 0 && <div className={card} data-testid="bookings-empty">No bookings in this view.</div>}
              {filteredBookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`admin-booking-${b.confirmation_code}`}
                  onClick={() => setSelectedBooking(b)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") setSelectedBooking(b); }}
                  className={`${card} flex flex-col lg:flex-row lg:items-center justify-between gap-4 cursor-pointer transition-colors hover:border-primary/50`}
                >
                  <div>
                    <div className="font-heading text-lg text-primary">#{b.confirmation_code}</div>
                    <div className="text-foreground text-sm">{b.customer_name} · {b.customer_phone}</div>
                    <div className="text-muted-foreground text-sm">{b.quote.pickup} → {b.quote.dropoff || (b.quote.hours ? `${b.quote.hours}h hourly` : "—")}</div>
                    <div className="text-muted-foreground text-xs mt-1">{b.quote.date} {b.quote.time} · {b.quote.vehicle_name} · ${b.quote.total.toFixed(2)}{b.flight_number ? ` · flight ${b.flight_number}` : ""}</div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select value={b.status} onValueChange={(v) => setBookingStatus(b.id, v)}>
                      <SelectTrigger data-testid={`booking-status-${b.confirmation_code}`} className={`${fld} w-48`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10 text-foreground">
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages">
            <p className="text-muted-foreground text-sm mb-4">Contact-form messages from your website.</p>
            <div className="space-y-3">
              {messages.length === 0 && <div className={card} data-testid="messages-empty"><Inbox size={18} className="text-muted-foreground inline mr-2" />No messages yet.</div>}
              {messages.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className={card}>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="font-heading text-lg text-foreground">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"><Mail size={14} /> {m.email}</a>
                    {m.phone && <a href={`tel:${m.phone}`} className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"><Phone size={14} /> {m.phone}</a>}
                  </div>
                  <p className="text-foreground/85 text-sm whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* LEADS */}
          <TabsContent value="leads">
            <p className="text-muted-foreground text-sm mb-4">Abandoned quotes &mdash; visitors who requested a price but haven&apos;t booked yet. Follow up to close the trip.</p>
            <div className="space-y-3">
              {leads.length === 0 && <div className={card} data-testid="leads-empty">No leads captured yet.</div>}
              {leads.map((l) => {
                const q = l.quote || {};
                const dest = q.dropoff || (q.hours ? `${q.hours}h hourly` : "—");
                const booked = l.status === "booked";
                return (
                  <div key={l.id} data-testid={`admin-lead-${l.id}`} className={`${card} flex flex-col lg:flex-row lg:items-center justify-between gap-4`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-heading text-lg text-foreground">{l.name || "Guest"}</span>
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${booked ? "border-green-500/40 text-green-400" : "border-primary/40 text-primary"}`}>{booked ? "Booked" : "Open lead"}</span>
                      </div>
                      <div className="text-muted-foreground text-sm truncate">{q.pickup} → {dest}</div>
                      <div className="text-muted-foreground text-xs mt-1">
                        {q.date} {q.time} · {q.vehicle_name}{q.total != null ? ` · $${Number(q.total).toFixed(2)}` : ""}
                        {l.reminder_sent ? " · reminder sent" : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <a href={`mailto:${l.email}`} data-testid={`lead-email-${l.id}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"><Mail size={15} /> {l.email}</a>
                      {q.phone && <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"><Phone size={15} /> {q.phone}</a>}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* FLEET */}
          <TabsContent value="fleet">
            <div className="grid md:grid-cols-2 gap-5">
              {vehicles.map((v) => (
                <div key={v.id} className={card} data-testid={`admin-vehicle-${v.id}`}>
                  <div className="flex items-center gap-4 mb-4">
                    {v.image ? <img src={v.image} alt={v.name} className="w-20 h-14 object-cover border border-white/10" /> : <div className="w-20 h-14 border border-white/10 bg-secondary" />}
                    <div>
                      <div className="text-foreground">{v.name}</div>
                      <div className="text-xs text-muted-foreground">{v.model}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-muted-foreground">Name</Label><Input className={fld} value={v.name} onChange={(e) => updateVehField(v.id, "name", e.target.value)} /></div>
                    <div><Label className="text-xs text-muted-foreground">Model</Label><Input className={fld} value={v.model} onChange={(e) => updateVehField(v.id, "model", e.target.value)} /></div>
                    <div><Label className="text-xs text-muted-foreground">Passengers</Label><Input type="number" className={fld} value={v.pax} onChange={(e) => updateVehField(v.id, "pax", Number(e.target.value))} /></div>
                    <div><Label className="text-xs text-muted-foreground">Luggage</Label><Input type="number" className={fld} value={v.luggage} onChange={(e) => updateVehField(v.id, "luggage", Number(e.target.value))} /></div>
                    <div><Label className="text-xs text-muted-foreground">Reservation Fee ($)</Label><Input type="number" step="1" data-testid={`veh-res-${v.id}`} className={fld} value={v.reservation_fee ?? 30} onChange={(e) => updateVehField(v.id, "reservation_fee", Number(e.target.value))} /></div>
                    <div><Label className="text-xs text-muted-foreground">Per Mile ($)</Label><Input type="number" step="0.05" data-testid={`veh-mile-${v.id}`} className={fld} value={v.per_mile ?? 2.75} onChange={(e) => updateVehField(v.id, "per_mile", Number(e.target.value))} /></div>
                    <div><Label className="text-xs text-muted-foreground">Extra Stop ($)</Label><Input type="number" step="1" data-testid={`veh-stop-${v.id}`} className={fld} value={v.stop_fee ?? 10} onChange={(e) => updateVehField(v.id, "stop_fee", Number(e.target.value))} /></div>
                    <div><Label className="text-xs text-muted-foreground">Hourly Rate ($)</Label><Input type="number" step="1" data-testid={`veh-hourly-${v.id}`} className={fld} value={v.hourly_rate ?? 85} onChange={(e) => updateVehField(v.id, "hourly_rate", Number(e.target.value))} /></div>
                    <div className="flex items-end gap-2 pb-1"><Switch checked={v.active} onCheckedChange={(c) => updateVehField(v.id, "active", c)} /><span className="text-sm text-muted-foreground">Active</span></div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button data-testid={`save-vehicle-${v.id}`} onClick={() => saveVehicle(v)} className="bg-primary text-primary-foreground rounded-none flex-1"><Save size={15} className="mr-1" /> Save</Button>
                    <Button variant="outline" onClick={() => deleteVehicle(v.id)} className="border-white/20 rounded-none hover:border-red-400 hover:text-red-400"><Trash2 size={15} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* PRICING & PROMOS */}
          <TabsContent value="pricing">
            <div className="grid lg:grid-cols-2 gap-6">
              {settings && (
                <div className={card}>
                  <h3 className="text-xl text-foreground mb-5">Pricing Settings</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[["minimum_miles", "Minimum Billed Miles"], ["minimum_fare", "Minimum Fare Floor ($)"], ["gratuity_rate", "Default Tip Rate (0-1)"], ["meet_greet_fee", "Meet & Greet ($)"], ["hourly_min_hours", "Hourly Min (hrs)"], ["hourly_free_miles_per_hour", "Free Miles / Hour"]].map(([k, lbl]) => (
                      <div key={k}>
                        <Label className="text-xs text-muted-foreground">{lbl}</Label>
                        <Input type="number" step="0.01" data-testid={`setting-${k}`} className={fld} value={settings[k]} onChange={(e) => setSettings({ ...settings, [k]: Number(e.target.value) })} />
                      </div>
                    ))}
                  </div>
                  <Button data-testid="save-settings" onClick={saveSettings} className="mt-5 w-full bg-primary text-primary-foreground rounded-none uppercase tracking-wide"><Save size={15} className="mr-1" /> Save Pricing</Button>
                </div>
              )}

              <div className={card}>
                <h3 className="text-xl text-foreground mb-5">Promo Codes</h3>
                <div className="space-y-3 mb-5">
                  {promos.map((p) => (
                    <div key={p.code} data-testid={`admin-promo-${p.code}`} className="flex items-center justify-between border border-white/10 px-4 py-3">
                      <div><span className="text-primary font-heading">{p.code}</span> <span className="text-muted-foreground text-sm">· {p.discount_pct}% off</span></div>
                      <div className="flex items-center gap-3">
                        <Switch checked={p.active} onCheckedChange={() => togglePromo(p)} />
                        <Button variant="ghost" size="icon" onClick={() => deletePromo(p.code)} className="hover:text-red-400"><Trash2 size={15} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-end border-t border-white/10 pt-4">
                  <div className="flex-1"><Label className="text-xs text-muted-foreground">Code</Label><Input data-testid="new-promo-code" className={fld} value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })} placeholder="SUMMER25" /></div>
                  <div className="w-24"><Label className="text-xs text-muted-foreground">% Off</Label><Input type="number" data-testid="new-promo-pct" className={fld} value={newPromo.discount_pct} onChange={(e) => setNewPromo({ ...newPromo, discount_pct: e.target.value })} /></div>
                  <Button data-testid="add-promo" onClick={addPromo} className="bg-primary text-primary-foreground rounded-none h-11"><Plus size={16} /></Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <BookingDetailsDialog booking={selectedBooking} onOpenChange={(o) => !o && setSelectedBooking(null)} />
    </div>
  );
}
